import { resourceService } from '@/services/resource.service'
import type { ResourceCategory, ResourceCreate, ResourceUploadUrlRequest } from '@/types'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

export const resourceKeys = {
  all: ['resources'] as const,
  list: (category?: ResourceCategory) => [...resourceKeys.all, 'list', category] as const,
  adminList: () => [...resourceKeys.all, 'admin', 'list'] as const,
}

export function useResources(category?: ResourceCategory) {
  return useQuery({
    queryKey: resourceKeys.list(category),
    queryFn: () => resourceService.listResources(category),
    staleTime: 1000 * 60 * 50,
  })
}

export function useAdminResources() {
  return useQuery({
    queryKey: resourceKeys.adminList(),
    queryFn: () => resourceService.adminListResources(),
  })
}

export function useAdminCreateResource() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: ResourceCreate) => resourceService.adminCreateResource(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: resourceKeys.adminList() }),
  })
}

export function useAdminGetUploadUrl() {
  return useMutation({
    mutationFn: ({ resourceId, data }: { resourceId: number; data: ResourceUploadUrlRequest }) =>
      resourceService.adminGetUploadUrl(resourceId, data),
  })
}

export function useAdminToggleResource() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, isActive }: { id: number; isActive: boolean }) =>
      resourceService.adminToggle(id, isActive),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: resourceKeys.adminList() }),
  })
}

export function useAdminImportResources() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (file: File) => resourceService.adminImportResourcesCsv(file),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: resourceKeys.adminList() }),
  })
}
